import React from 'react';
import InputField from 'Core/components/FormElements/InputField';
import { useTranslation } from 'react-i18next';
import SelectMenu from 'Core/components/FormElements/SelectMenu';
import { AlertError, Alert } from 'Core/components/Alert';
import InfoBox from 'Core/components/InfoBox';
import { PrimaryButton } from 'Core/components/Button';
import { usStates } from 'Modules/creditDebit/config/usStates';
import Page from 'Core/components/Page';
import BirthDateInput from 'Core/components/FormElements/BirthDateInput';
import { Column, Row } from 'Core/components/Layout';
import { StyledFormSection } from 'Core/components/FormElements/formElements.styled';
import TermsInput from 'Core/components/FormElements/TermsInput';
import Loader from 'Core/components/Loader';
import { useNavigate } from 'react-location';

import useCreateApplicationForm from 'Modules/lendFlow/hooks/useCreateApplicationForm';
import useCreateApplication from 'Modules/lendFlow/hooks/useCreateApplication';
import useGetCustomerInfo from 'Modules/lendFlow/hooks/useGetCustomerInfo';
import useGetApplication from 'Modules/lendFlow/hooks/useGetApplication';
import { FaEye, FaEyeSlash } from 'react-icons/fa6';
import Quote from 'Core/components/Quote';
import { useScrollTop } from 'Core/hooks/useScrollTop';
import { relationTypes } from '../config/relationTypes';
import { ImInfo } from 'react-icons/im';
import classNames from 'classnames';
import { mapCustomerData } from 'Modules/lendFlow/utils/mapCustomerData';
import { actions, features, postLogMessage } from 'Core/utils/logger';
import constants from '../config/constants';
import cacheKeys from 'Core/hooks/queries/cacheKeys';
import { useQueryClient } from 'react-query';

const dateFormat = 'yyyy-MM-dd';

const CreateApplication = ({
  type = 'primary',
  customer_id = '',
  purpose = 'create',
  errorConfig = {},
}) => {
  document.title = 'Payment Portal - LendFlow Application';
  const navigate = useNavigate();
  const { t } = useTranslation();
  const scroll = useScrollTop();
  const { mutateAsync: createApplication, isLoading: isCreateAppLoading } =
    useCreateApplication(purpose);
  const [requestId, setRequestId] = React.useState('');
  const [showSsn, setShowSsn] = React.useState(false);
  const [showRelationField, setshowRelationField] = React.useState(type !== 'primary');
  const [createApplicationPolling, setCreateApplicationPolling] = React.useState(false);
  const {
    isSuccess: isGetAppSuccess,
    isLoading: isGetAppLoading,
    data: getAppData,
  } = useGetApplication(requestId, createApplicationPolling ? 3000 : false);
  const { data: customerInfo } = useGetCustomerInfo();
  const [dobDefaults, setDobDefaults] = React.useState({ day: '01', month: '01', year: '' });
  const [isFinanceAppLoading, setIsFinanceAppLoading] = React.useState(false);
  const [initialAppStatus, setInitialAppStatus] = React.useState({
    nextAction: null,
    message: null,
    status: null,
  });
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitted },
  } = useCreateApplicationForm(errorConfig);
  const queryClient = useQueryClient();
  const selectedRelationship = watch('relationship');
  const relationshipSessionStorageString = sessionStorage.getItem('relationship');
  const previousRelationship = JSON.parse(relationshipSessionStorageString)?.relationship || '';
  const sessionStorageCustomerId = JSON.parse(relationshipSessionStorageString)?.customer_id || '';
  const filteredRelationTypes = relationTypes.filter((item) =>
    !previousRelationship || type === 'primary' ? true : item.value !== previousRelationship
  );

  const showUpdateFormMessage = !!errorConfig.type;

  const initializeAppStatus = (appData) => {
    const { correction_required: { status = '', message = '' } = {} } = appData?.payload || {};

    setInitialAppStatus({
      nextAction: appData?.next_action,
      message,
      status,
    });
  };

  React.useEffect(() => {
    const isAppStatusChanged = () => {
      return (
        getAppData?.next_action !== initialAppStatus.nextAction ||
        getAppData?.message !== initialAppStatus.message ||
        getAppData?.status !== initialAppStatus.status
      );
    };

    if (isGetAppSuccess && createApplicationPolling && isAppStatusChanged()) {
      sessionStorage.setItem(constants.SESS_STORAGE_APPLICATION_KEY, JSON.stringify(getAppData));
      queryClient.invalidateQueries(cacheKeys.financingNextStep);
      setRequestId('');
      setCreateApplicationPolling(false);
      setIsFinanceAppLoading(false);
    }
  }, [
    isGetAppSuccess,
    getAppData,
    navigate,
    createApplicationPolling,
    requestId,
    initialAppStatus,
  ]);

  React.useEffect(() => {
    if (isGetAppLoading) {
      setIsFinanceAppLoading(true);
    }
  }, [isGetAppLoading]);

  React.useEffect(() => {
    return () => {
      setRequestId('');
      queryClient.removeQueries(cacheKeys.lendflowGetApplication);
    };
  }, [queryClient]);

  const parseDateOfBirth = (dateOfBirth) => {
    let year, day, month;
    if (Array.isArray(dateOfBirth)) {
      [year] = dateOfBirth;
      [, day, month] = dateOfBirth;
      if (month > 0 && month < 10) {
        month = '0' + month;
      }
      if (day > 0 && day < 10) {
        day = '0' + day;
      }
    } else if (typeof dateOfBirth === 'string') {
      [year, month, day] = dateOfBirth.split('-');
    }
    return { day, month, year };
  };
  React.useEffect(() => {
    const mappedCustomerData = mapCustomerData(customerInfo, customer_id);

    if (mappedCustomerData) {
      if (
        mappedCustomerData.relationship === previousRelationship &&
        sessionStorageCustomerId === customer_id &&
        type !== 'primary'
      ) {
        mappedCustomerData.relationship = null;
      }

      Object.keys(mappedCustomerData).forEach((field) =>
        setValue(field, mappedCustomerData[field])
      );
      const finalDateOfBirth = mappedCustomerData.date_of_birth || mappedCustomerData.dateOfBirth;
      if (finalDateOfBirth) {
        const { day, month, year } = parseDateOfBirth(finalDateOfBirth);
        setDobDefaults({ day, month, year });
      }
    } else {
      setDobDefaults({ day: '01', month: '01', year: '' });
      reset(mapCustomerData({}, customer_id));
    }
  }, [
    customerInfo,
    reset,
    setValue,
    isGetAppSuccess,
    isGetAppLoading,
    navigate,
    requestId,
    getAppData?.next_action,
    customer_id,
    previousRelationship,
    sessionStorageCustomerId,
    type,
  ]);

  React.useEffect(() => {
    const errorFields = Object.keys(errors);

    if (errorFields?.length) {
      const errorMap = errorFields.map((field) => {
        return { field, message: errors[field].message };
      });
      console.log('form errors!', errorMap);
      postLogMessage({
        feature: features.createAllegroApp,
        action: actions.createApplicationFormError,
        errors: errorMap,
      });
    }
  }, [errors]);
  const handleRelationshipChange = () => {
    if (customerInfo && customerInfo.relationship === selectedRelationship) {
      const customerData = mapCustomerData(customerInfo, customer_id);
      Object.keys(customerData).forEach((field) => setValue(field, customerData[field]));

      if (customerData.date_of_birth || customerData.dateOfBirth) {
        const finalDateOfBirth = customerData.date_of_birth || customerData.dateOfBirth;
        const { day, month, year } = parseDateOfBirth(finalDateOfBirth);
        setDobDefaults({ day, month, year });
      }
    } else {
      setDobDefaults({ day: '01', month: '01', year: '' });
      reset(mapCustomerData({}, customer_id));
    }
  };

  const ssn = watch('socialSecurityNumber');
  React.useEffect(() => {
    setValue('socialSecurityNumber', ssn?.replace(/\D+/g, ''));
  }, [setValue, ssn]);

  const phone = watch('phone');
  React.useEffect(() => {
    if (phone?.startsWith('+1')) {
      setValue('phone', phone.replace(/^\+1/, ''));
    }
  }, [setValue, phone]);

  const hasFormErrors = Object.keys(errors).length > 0;

  const storeCustomerInfo = (formData) => {
    const filteredFormData = JSON.parse(JSON.stringify(formData));
    delete filteredFormData.socialSecurityNumber;
    delete filteredFormData.annualIncome;

    sessionStorage.setItem(
      constants.SESS_STORAGE_CUSTOMER_INFO_KEY,
      JSON.stringify({ ...filteredFormData, customer_id })
    );
  };

  const onSubmit = async (formData) => {
    setRequestId('');
    initializeAppStatus();
    storeCustomerInfo(formData);

    setCreateApplicationPolling(true);
    scroll.top();
    const {
      headers: { request_id },
    } = await createApplication(formData);

    setRequestId(request_id);
  };

  const toggleSsnDisplay = () => {
    setShowSsn(!showSsn);
  };

  return (
    <Page
      title="LendFlow - Create Application"
      subtitle="Global Multi-Provider Financing Application"
      withStepProgress={false}
      stickyFooter
      branding="lendflow"
    >
      {isCreateAppLoading && <Loader message="Submitting your application to providers..." />}
      {isFinanceAppLoading && <Loader message={t('status.loading.financingApplication')} />}

      {/* Info Banner */}
      <div className="bg-lendflow-surface border-l-4 border-lendflow-bg rounded-r-lg p-4 mb-6">
        <p className="text-sm text-lendflow-dark font-medium">
          Your application will be evaluated by multiple financing providers to find the best terms
          for you.
        </p>
      </div>

      <form name="lendFlowCreateApplication" onSubmit={handleSubmit(onSubmit)}>
        {hasFormErrors && <AlertError type="error">{t('status.error.checkErrors')}</AlertError>}
        {showUpdateFormMessage && <Alert type="error">{errorConfig.message}</Alert>}
        <StyledFormSection>
          <Row>
            {!showRelationField && (
              <Column className="text-gray-500">
                <div className="flex items-center my-2">
                  <span className="inline-block pr-2">
                    {' '}
                    <ImInfo />
                  </span>
                  Filling the application for someone else?
                  <button
                    className="text-lendflow-bg hover:text-lendflow-dark hover:underline mx-1 font-medium"
                    type="button"
                    onClick={() => setshowRelationField(true)}
                  >
                    Click here
                  </button>{' '}
                  to change relationship
                </div>
              </Column>
            )}

            <Column className="!mb-0">
              <Alert type="infoLight">{t('patientfi.createApplication.enterLegalNames')}</Alert>
            </Column>

            <Column className={classNames('md:!flex-col-6', { hidden: !showRelationField })}>
              <SelectMenu
                register={register}
                label={t('common.relationship')}
                name="relationship"
                items={filteredRelationTypes}
                onChange={handleRelationshipChange}
                errors={errors}
              />
            </Column>

            <Column className="!mb-2"></Column>
            <Column className="md:!flex-col-6">
              <InputField
                register={register}
                label={t('common.firstName')}
                name="firstName"
                errors={errors}
              />
            </Column>

            <Column className="md:!flex-col-6">
              <InputField
                register={register}
                label={t('common.lastName')}
                name="lastName"
                errors={errors}
              />
            </Column>

            <Column className="md:!flex-col-3">
              <InputField
                register={register}
                label={t('common.street')}
                name="address"
                errors={errors}
              />
            </Column>

            <Column className="md:!flex-col-3">
              <InputField
                register={register}
                label={t('common.city')}
                name="city"
                errors={errors}
              />
            </Column>

            <Column className="md:!flex-col-3">
              <SelectMenu
                register={register}
                label={t('common.state')}
                name="state"
                items={usStates}
                errors={errors}
              />
            </Column>

            <Column className="md:!flex-col-3">
              <InputField
                register={register}
                label={t('common.postalCode')}
                name="zip"
                errors={errors}
              />
            </Column>
          </Row>

          <hr />

          <Row>
            <Column className="md:!flex-col-6">
              <InputField
                register={register}
                label={t('common.phone')}
                name="phone"
                errors={errors}
              />
            </Column>
            <Column className="md:!flex-col-6">
              <InputField
                register={register}
                label={t('common.email')}
                name="email"
                errors={errors}
              />
            </Column>
          </Row>

          <Row>
            <Column className="md:!flex-col-6 flex items-end">
              <InputField
                className="!rounded-r-none"
                register={register}
                label={t('patientfi.formlabel.ssn')}
                name="socialSecurityNumber"
                errors={errors}
                onAppendClick={toggleSsnDisplay}
                appendText={showSsn ? <FaEyeSlash /> : <FaEye />}
                type={showSsn ? 'text' : 'password'}
              />
            </Column>

            <Column className="md:!flex-col-6">
              <BirthDateInput
                format={dateFormat}
                register={register}
                name="dateOfBirth"
                padZeros
                onChange={(value) => {
                  setValue('dateOfBirth', value, { shouldValidate: isSubmitted });
                }}
                defaultValues={dobDefaults}
                errors={errors}
              />
            </Column>
          </Row>

          <hr />

          <Row>
            <Column className="md:!flex-col-6">
              <InputField
                register={register}
                label={t('common.annualIncome')}
                name="annualIncome"
                type="number"
                errors={errors}
              />
            </Column>
            <Column className="md:!flex-col-12">
              <Quote showWhitespace>{t('patientfi.createApplication.readToCustomer.note2')}</Quote>
            </Column>
          </Row>

          <hr />

          <div className="mt-2">
            <h3>{t('common.noticeToApplicants')}</h3>
            <Alert type="warning">
              <span className="font-semibold text-sm text-black">
                {t('common.readToCustomer.doubleTitle')}
              </span>
            </Alert>
            <div className="mt-4">
              <InfoBox showBorder>{t('patientfi.createApplication.readToCustomer.note3')}</InfoBox>
            </div>
            <div className="mt-4">
              <InfoBox showBorder>{t('patientfi.createApplication.readToCustomer.note4')}</InfoBox>
            </div>
          </div>
          <hr />
          <TermsInput name="terms" register={register} errors={errors}>
            <b>{t('financing.nextgen.createApplication.terms')}</b>
            <ol className="list-decimal ml-4 mt-2">
              <li>{t('patientfi.createApplication.terms.note1')}</li>
              <li>{t('patientfi.createApplication.terms.note2')}</li>
            </ol>
          </TermsInput>
        </StyledFormSection>

        <Row>
          <Column className="md:!flex-col-12 !flex gap-1 !justify-end">
            <PrimaryButton type="submit" data-testid="submit-lendflow-application">
              {t('common.submitPrequalificationApplication')}
            </PrimaryButton>
          </Column>
        </Row>
      </form>
    </Page>
  );
};

export default CreateApplication;
